import React, { useState } from "react";
import { motion } from "framer-motion";
import SafeAnimatePresence from "../../../components/ui/SafeAnimatePresence";
import { FiPlus, FiMic, FiSearch } from "react-icons/fi";
import type { VoiceAgent, PhoneNumber } from "../voice.types";
import AgentCard from "./AgentCard";
import AgentEditPanel from "./AgentEditPanel";
import AgentCreatePanel from "./AgentCreatePanel";
import { deleteAgentDF } from "../../../services/voiceDataFabricService";
import "../../../styles/Voice.css";

interface AgentsTabProps {
  agents: VoiceAgent[];
  phones: PhoneNumber[];
  search: string;
  onSearchChange: (v: string) => void;
  onEdit: () => void;
  onCreate: () => void;
  onAgentCreated?: () => void;
  onDeleteSuccess?: (name: string) => void;
  onPanelChange?: (title: string | null) => void;
}

const AgentsTab: React.FC<AgentsTabProps> = ({
  agents,
  phones,
  search,
  onSearchChange,
  onCreate,
  onAgentCreated,
  onDeleteSuccess,
  onPanelChange,
}) => {
  const [editingAgent,  setEditingAgent]  = useState<VoiceAgent | null>(null);
  const [creatingAgent, setCreatingAgent] = useState(false);

  const openCreate = () => { setCreatingAgent(true);  onPanelChange?.("Create New Agent"); };
  const openEdit   = (a: VoiceAgent) => { setEditingAgent(a); onPanelChange?.("Edit Agent"); };
  const closePanel = () => { setEditingAgent(null); setCreatingAgent(false); onPanelChange?.(null); };

  const filtered = search.trim()
    ? agents.filter((a) =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.welcomeMessage?.toLowerCase().includes(search.toLowerCase())
      )
    : agents;

  const phoneFor = (agent: VoiceAgent): string | null =>
    phones.find((p) => p.assignedAgentId === agent.elevenLabsAgentId)
      ?.phoneNumber ?? null;

  const handleDeleteAgent = async (agent: VoiceAgent) => {
    await deleteAgentDF(agent.elevenLabsAgentId);
    onAgentCreated?.();
    onDeleteSuccess?.(agent.name);
  };

  return (
    <motion.div
      key="agents"
      className="voice-tab-content"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
    >
      <SafeAnimatePresence mode="wait">
        {editingAgent ? (
          <AgentEditPanel
            key="edit-panel"
            agent={editingAgent}
            onBack={() => { closePanel(); onAgentCreated?.(); }}
          />
        ) : creatingAgent ? (
          <AgentCreatePanel
            key="create-panel"
            onBack={closePanel}
            onCreated={() => { closePanel(); onAgentCreated?.(); }}
          />
        ) : (
          <motion.div
            key="agents-grid-view"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.18 }}
          >
            {/* Hidden trigger button — clicked by the hero-row New Agent button */}
            <button
              id="voice-new-agent-trigger"
              style={{ display: "none" }}
              onClick={openCreate}
            />

            {agents.length === 0 ? (
              <div className="voice-empty">
                <div className="voice-empty__icon">
                  <FiMic size={28} />
                </div>
                <div className="voice-empty__title">No voice agents yet</div>
                <div className="voice-empty__desc">
                  Create an agent in Momentum Voice to get started.
                </div>
                <button className="glass-btn voice-btn-purple" onClick={openCreate}>
                  <FiPlus size={13} /> New Agent
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="voice-empty">
                <div className="voice-empty__icon"><FiSearch size={24} /></div>
                <div className="voice-empty__title">No agents found</div>
                <div className="voice-empty__desc">Try a different search term.</div>
              </div>
            ) : (
              <div className="voice-agents-grid">
                <SafeAnimatePresence>
                  {filtered.map((agent) => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      assignedPhone={phoneFor(agent)}
                      onEdit={openEdit}
                      onDelete={handleDeleteAgent}
                    />
                  ))}
                </SafeAnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </SafeAnimatePresence>
    </motion.div>
  );
};

export default AgentsTab;
